import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { 
  ArrowLeft,
  Save, 
  Copy, 
  Zap, 
  Trash2, 
  Check, 
  Eye, 
  EyeOff, 
  ShoppingBag,
  Package,
  CreditCard,
  ShieldCheck,
  Globe,
  Info
} from "lucide-react";
import ApiClient from "../utils/api";
import { useToast } from "../context/ToastContext";
import DeleteConfirmModal from "../components/DeleteConfirmModal";

const UNCHANGED_KEY = "__UNCHANGED__";
const MASK_DISPLAY = "••••••••••••••••••••••••••••••••";

const ShopifyConfig = ({ activeAiAgentId }) => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [shopifyConfig, setShopifyConfig] = useState({
    shop_domain: "",
    access_token: "",
    api_key: "",
    api_secret: "",
    order_tracking_enabled: true,
    product_sync_enabled: true,
    draft_orders_enabled: true,
    is_active: false,
  });

  const [webhookUrl, setWebhookUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const loadConfig = useCallback(async () => {
    try {
      const response = await ApiClient.request("/shopify/config", {
        aiAgentId: activeAiAgentId
      });
      if (response && response.success && response.config) {
        setShopifyConfig({
          shop_domain: response.config.shop_domain || "",
          access_token: response.config.access_token ? UNCHANGED_KEY : "",
          api_key: response.config.api_key || "",
          api_secret: response.config.api_secret ? UNCHANGED_KEY : "",
          order_tracking_enabled: response.config.order_tracking_enabled !== false,
          product_sync_enabled: response.config.product_sync_enabled !== false,
          draft_orders_enabled: response.config.draft_orders_enabled !== false,
          is_active: Boolean(response.config.is_active),
        });
        setWebhookUrl(response.config.webhook_url || "");
      }
    } catch (error) {
      console.log("Shopify config not found or error loading");
    }
  }, [activeAiAgentId]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const handleDomainChange = (e) => {
    let val = e.target.value.trim().toLowerCase();
    val = val.replace(/^https?:\/\//, "");
    val = val.replace(/\/$/, "");
    setShopifyConfig(prev => ({ ...prev, shop_domain: val }));
  };

  const saveConfig = async () => {
    if (!shopifyConfig.shop_domain?.trim()) {
      showToast("Please enter your Shopify store domain (e.g. store.myshopify.com)", "error");
      return;
    }
    if (!shopifyConfig.access_token?.trim()) {
      showToast("Please enter your Admin API Access Token", "error");
      return;
    }

    try {
      setSaving(true);
      const payload = {
        shop_domain: shopifyConfig.shop_domain.trim(),
        api_key: shopifyConfig.api_key?.trim() || null,
        order_tracking_enabled: shopifyConfig.order_tracking_enabled,
        product_sync_enabled: shopifyConfig.product_sync_enabled,
        draft_orders_enabled: shopifyConfig.draft_orders_enabled,
        is_active: shopifyConfig.is_active,
      };

      if (shopifyConfig.access_token !== UNCHANGED_KEY) {
        payload.access_token = shopifyConfig.access_token.trim();
      }
      if (shopifyConfig.api_secret !== UNCHANGED_KEY) {
        payload.api_secret = shopifyConfig.api_secret?.trim() || null;
      }

      const response = await ApiClient.request("/shopify/config", {
        method: "PUT",
        body: JSON.stringify(payload),
        aiAgentId: activeAiAgentId
      });

      if (response && response.success) {
        setWebhookUrl(response.webhook_url || "");
        showToast("Shopify store configuration saved successfully!", "success");
        await loadConfig();
      } else {
        showToast("Failed to save Shopify configuration", "error");
      }
    } catch (error) {
      const msg = error?.message || error?.detail || "Failed to save configuration";
      showToast(msg, "error");
    } finally {
      setSaving(false);
    }
  };

  const deleteConfig = async () => {
    try {
      setSaving(true);
      const response = await ApiClient.request("/shopify/config", {
        method: "DELETE",
        aiAgentId: activeAiAgentId
      });

      if (response && response.success) {
        setShopifyConfig({
          shop_domain: "",
          access_token: "",
          api_key: "",
          api_secret: "",
          order_tracking_enabled: true,
          product_sync_enabled: true,
          draft_orders_enabled: true,
          is_active: false,
        });
        setWebhookUrl("");
        showToast("Shopify integration removed successfully!", "success");
      }
    } catch (error) {
      // Handled globally
    } finally {
      setSaving(false);
      setIsDeleteModalOpen(false);
    }
  };

  const toggleActive = async () => {
    const nextState = !shopifyConfig.is_active;
    if (nextState) {
      if (!shopifyConfig.shop_domain?.trim() || !shopifyConfig.access_token?.trim()) {
        showToast("Please enter and save store domain and access token first", "error");
        return;
      }
      if (shopifyConfig.access_token !== UNCHANGED_KEY) {
        showToast("Please save your configuration changes before activating", "warning");
        return;
      }
    }

    try {
      const response = await ApiClient.request("/shopify/config", {
        method: "PUT",
        body: JSON.stringify({
          ...shopifyConfig,
          is_active: nextState
        }),
        aiAgentId: activeAiAgentId
      });

      if (response && response.success) {
        setShopifyConfig(prev => ({ ...prev, is_active: nextState }));
        showToast(nextState ? "Shopify integration activated!" : "Shopify integration paused", "success");
      } else {
        showToast("Failed to update integration status", "error");
      }
    } catch (error) {
      const msg = error?.message || error?.detail || "Failed to update integration status";
      showToast(msg, "error");
    }
  };

  const toggleCapability = async (key, label) => {
    const nextValue = !shopifyConfig[key];

    const hasCredentials = Boolean(
      shopifyConfig.shop_domain?.trim() && 
      (shopifyConfig.access_token === UNCHANGED_KEY || shopifyConfig.access_token?.trim())
    );

    if (!hasCredentials) {
      showToast(`Please save Shopify credentials first before configuring ${label}`, "error");
      return;
    }

    if (shopifyConfig.access_token !== UNCHANGED_KEY) {
      setShopifyConfig(prev => ({ ...prev, [key]: nextValue }));
      showToast(`${label} ${nextValue ? "enabled" : "disabled"}. Click 'Save' to apply changes.`, "info");
      return;
    }

    try {
      const response = await ApiClient.request("/shopify/config", {
        method: "PUT",
        body: JSON.stringify({
          [key]: nextValue
        }),
        aiAgentId: activeAiAgentId
      });

      if (response && response.success) {
        setShopifyConfig(prev => ({ ...prev, [key]: nextValue }));
        showToast(`${label} ${nextValue ? "enabled" : "disabled"} successfully!`, "success");
      } else {
        showToast(`Failed to update ${label}`, "error");
      }
    } catch (error) {
      const errorMsg = error?.message || error?.detail || `Failed to update ${label}`;
      showToast(errorMsg, "error");
    }
  };

  const getDisplayWebhookUrl = () => {
    if (!webhookUrl) return "";
    let path = webhookUrl;
    if (webhookUrl.startsWith("http://") || webhookUrl.startsWith("https://")) {
      try {
        const urlObj = new URL(webhookUrl);
        path = urlObj.pathname + urlObj.search;
      } catch (e) {}
    }
    const baseUrl = process.env.REACT_APP_API_BASE_URL || window.location.origin;
    let cleanBase = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
    let cleanPath = path.startsWith("/") ? path : "/" + path;
    if (cleanBase.endsWith("/api") && cleanPath.startsWith("/api")) {
      cleanBase = cleanBase.slice(0, -4);
    }
    return cleanBase + cleanPath;
  };

  const displayUrl = getDisplayWebhookUrl();

  const copyWebhookUrl = async () => {
    if (!displayUrl) return;
    try {
      await navigator.clipboard.writeText(displayUrl);
      setCopied(true);
      showToast("Webhook URL copied to clipboard!", "success");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      showToast("Failed to copy URL", "error");
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-white font-['Inter',sans-serif] animate-slideIn">
      <div className="w-full max-w-7xl mx-auto px-4 py-5 sm:px-6 sm:py-6 lg:px-8 space-y-4 sm:space-y-6">
        
        {/* Top Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-4 sm:pb-5 border-b border-zinc-100 gap-3 sm:gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/integrations")}
              className="p-1.5 text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100 rounded-lg transition-colors"
              title="Back to Integrations"
            >
              <ArrowLeft size={16} />
            </button>
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-zinc-900 tracking-tight">Shopify Store Integration</h1>
              <p className="text-xs text-zinc-500 mt-0.5">
                Empower your AI agent with live order tracking, product lookup, and checkout support.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2.5 self-start sm:self-auto pl-9 sm:pl-0">
            <span className={`px-2 py-0.5 rounded text-[10px] font-semibold tracking-wide uppercase border ${
              shopifyConfig.is_active
                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                : "bg-zinc-50 text-zinc-500 border-zinc-200"
            }`}>
              {shopifyConfig.is_active ? "Active" : "Disabled"}
            </span>
            <button
              type="button"
              onClick={toggleActive}
              className={`w-8 h-4.5 rounded-full transition-colors relative flex-shrink-0 ${
                shopifyConfig.is_active ? "bg-zinc-900" : "bg-zinc-200"
              }`}
              title={shopifyConfig.is_active ? "Deactivate integration" : "Activate integration"}
            >
              <div
                className={`absolute top-0.5 w-3.5 h-3.5 bg-white rounded-full transition-transform ${
                  shopifyConfig.is_active ? "left-4" : "left-0.5"
                }`}
              />
            </button>
          </div>
        </div>

        {/* Credentials Form */}
        <div className="bg-white rounded-xl border border-zinc-200 p-4 sm:p-6 space-y-5">
          <div className="flex items-center gap-2.5 pb-3 border-b border-zinc-100">
            <ShieldCheck className="text-zinc-800 shrink-0" size={18} />
            <div>
              <h2 className="text-xs font-semibold text-zinc-900 uppercase tracking-wider">Shopify Store Credentials</h2>
              <p className="text-[11px] text-zinc-500">Provide your store domain and custom app Admin API access token</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Store Domain */}
            <div>
              <label className="block text-xs font-medium text-zinc-700 mb-1.5">
                Store Domain <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={shopifyConfig.shop_domain}
                onChange={handleDomainChange}
                placeholder="store-name.myshopify.com"
                className="w-full px-3.5 py-2 text-xs border border-zinc-200 rounded-lg bg-zinc-50/50 focus:bg-white focus:outline-none focus:border-zinc-900 text-zinc-900 font-mono transition-colors"
              />
              <p className="text-[11px] text-zinc-400 mt-1">
                Your <code className="text-zinc-600 bg-zinc-100 px-1 py-0.5 rounded text-[10px]">.myshopify.com</code> store address.
              </p>
            </div>

            {/* Admin API Access Token */}
            <div>
              <label className="block text-xs font-medium text-zinc-700 mb-1.5">
                Admin API Access Token <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showToken ? "text" : "password"}
                  value={shopifyConfig.access_token === UNCHANGED_KEY ? MASK_DISPLAY : shopifyConfig.access_token}
                  onChange={(e) => {
                    setShopifyConfig(prev => ({ ...prev, access_token: e.target.value }));
                  }}
                  onFocus={() => {
                    if (shopifyConfig.access_token === UNCHANGED_KEY) {
                      setShopifyConfig(prev => ({ ...prev, access_token: "" }));
                    }
                  }}
                  placeholder={shopifyConfig.access_token === UNCHANGED_KEY ? MASK_DISPLAY : "shpat_xxxxxxxxxxxxxxxxxxxxxxxx"}
                  className="w-full px-3.5 py-2 pr-10 text-xs border border-zinc-200 rounded-lg bg-zinc-50/50 focus:bg-white focus:outline-none focus:border-zinc-900 text-zinc-900 font-mono transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowToken(!showToken)}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700 p-1 transition-colors"
                >
                  {showToken ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
              <p className="text-[11px] text-zinc-400 mt-1">
                Admin API token starting with <code className="text-zinc-600 bg-zinc-100 px-1 py-0.5 rounded text-[10px]">shpat_</code>.
              </p>
            </div>

            {/* API Key (Optional) */}
            <div>
              <label className="block text-xs font-medium text-zinc-700 mb-1.5">
                API Key / Client ID <span className="text-zinc-400 font-normal">(Optional)</span>
              </label>
              <input
                type="text"
                value={shopifyConfig.api_key}
                onChange={(e) => setShopifyConfig(prev => ({ ...prev, api_key: e.target.value }))}
                placeholder="Custom App Client ID"
                className="w-full px-3.5 py-2 text-xs border border-zinc-200 rounded-lg bg-zinc-50/50 focus:bg-white focus:outline-none focus:border-zinc-900 text-zinc-900 font-mono transition-colors"
              />
            </div>

            {/* API Secret Key (Optional) */}
            <div>
              <label className="block text-xs font-medium text-zinc-700 mb-1.5">
                API Secret Key <span className="text-zinc-400 font-normal">(Optional)</span>
              </label>
              <div className="relative">
                <input
                  type={showSecret ? "text" : "password"}
                  value={shopifyConfig.api_secret === UNCHANGED_KEY ? MASK_DISPLAY : shopifyConfig.api_secret}
                  onChange={(e) => setShopifyConfig(prev => ({ ...prev, api_secret: e.target.value }))}
                  onFocus={() => {
                    if (shopifyConfig.api_secret === UNCHANGED_KEY) {
                      setShopifyConfig(prev => ({ ...prev, api_secret: "" }));
                    }
                  }}
                  placeholder={shopifyConfig.api_secret === UNCHANGED_KEY ? MASK_DISPLAY : "Custom App Secret"}
                  className="w-full px-3.5 py-2 pr-10 text-xs border border-zinc-200 rounded-lg bg-zinc-50/50 focus:bg-white focus:outline-none focus:border-zinc-900 text-zinc-900 font-mono transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowSecret(!showSecret)}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700 p-1 transition-colors"
                >
                  {showSecret ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>
          </div>

          <div className="pt-3 border-t border-zinc-100 flex flex-col-reverse sm:flex-row sm:items-center justify-between gap-2.5">
            {shopifyConfig.shop_domain ? (
              <button
                type="button"
                onClick={() => setIsDeleteModalOpen(true)}
                disabled={saving}
                className="inline-flex items-center justify-center gap-1.5 px-3.5 py-2 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors disabled:opacity-40"
              >
                <Trash2 size={14} />
                <span>Disconnect Shopify</span>
              </button>
            ) : <div />}

            <button
              type="button"
              onClick={saveConfig}
              disabled={saving || !shopifyConfig.shop_domain?.trim() || !shopifyConfig.access_token?.trim()}
              className="inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-zinc-900 text-white rounded-lg hover:bg-zinc-800 disabled:opacity-40 text-xs font-medium transition-colors"
            >
              {saving ? (
                <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <Save size={14} />
              )}
              <span>{saving ? "Saving..." : "Save Configuration"}</span>
            </button>
          </div>
        </div>

        {/* Agent Capabilities Grid */}
        <div className="bg-white rounded-xl border border-zinc-200 p-4 sm:p-6 space-y-4">
          <div className="pb-3 border-b border-zinc-100">
            <h3 className="text-xs font-semibold text-zinc-900 uppercase tracking-wider flex items-center gap-2">
              <Zap size={14} className="text-zinc-600" />
              <span>Agent Capabilities</span>
            </h3>
            <p className="text-[11px] text-zinc-500 mt-0.5">
              Control what data and commerce actions the AI assistant can execute on your store.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {/* Order Tracking */}
            <div className="p-3.5 rounded-lg border border-zinc-200 bg-zinc-50/50 flex flex-col justify-between space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div className="p-1.5 rounded-md bg-blue-50 text-blue-700">
                  <Package size={16} />
                </div>
                <button
                  type="button"
                  onClick={() => toggleCapability("order_tracking_enabled", "Order Lookup & Tracking")}
                  className={`w-7 h-4 rounded-full transition-colors relative flex-shrink-0 ${
                    shopifyConfig.order_tracking_enabled ? "bg-zinc-900" : "bg-zinc-200"
                  }`}
                >
                  <div
                    className={`absolute top-0.5 w-3 h-3 bg-white rounded-full transition-transform ${
                      shopifyConfig.order_tracking_enabled ? "left-3.5" : "left-0.5"
                    }`}
                  />
                </button>
              </div>
              <div>
                <h4 className="text-xs font-semibold text-zinc-900">Order Tracking</h4>
                <p className="text-[11px] text-zinc-500 mt-0.5 leading-relaxed">
                  Look up order status, fulfillment tracking numbers, and delivery dates.
                </p>
              </div>
            </div>

            {/* Product Catalog */}
            <div className="p-3.5 rounded-lg border border-zinc-200 bg-zinc-50/50 flex flex-col justify-between space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div className="p-1.5 rounded-md bg-emerald-50 text-emerald-700">
                  <ShoppingBag size={16} />
                </div>
                <button
                  type="button"
                  onClick={() => toggleCapability("product_sync_enabled", "Product Catalog Sync")}
                  className={`w-7 h-4 rounded-full transition-colors relative flex-shrink-0 ${
                    shopifyConfig.product_sync_enabled ? "bg-zinc-900" : "bg-zinc-200"
                  }`}
                >
                  <div
                    className={`absolute top-0.5 w-3 h-3 bg-white rounded-full transition-transform ${
                      shopifyConfig.product_sync_enabled ? "left-3.5" : "left-0.5"
                    }`}
                  />
                </button>
              </div>
              <div>
                <h4 className="text-xs font-semibold text-zinc-900">Product Sync</h4>
                <p className="text-[11px] text-zinc-500 mt-0.5 leading-relaxed">
                  Query product variants, inventory availability, specifications, and prices.
                </p>
              </div>
            </div>

            {/* Draft Orders */}
            <div className="p-3.5 rounded-lg border border-zinc-200 bg-zinc-50/50 flex flex-col justify-between space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div className="p-1.5 rounded-md bg-purple-50 text-purple-700">
                  <CreditCard size={16} />
                </div>
                <button
                  type="button"
                  onClick={() => toggleCapability("draft_orders_enabled", "Direct Checkout & Draft Orders")}
                  className={`w-7 h-4 rounded-full transition-colors relative flex-shrink-0 ${
                    shopifyConfig.draft_orders_enabled ? "bg-zinc-900" : "bg-zinc-200"
                  }`}
                >
                  <div
                    className={`absolute top-0.5 w-3 h-3 bg-white rounded-full transition-transform ${
                      shopifyConfig.draft_orders_enabled ? "left-3.5" : "left-0.5"
                    }`}
                  />
                </button>
              </div>
              <div>
                <h4 className="text-xs font-semibold text-zinc-900">Draft Orders</h4>
                <p className="text-[11px] text-zinc-500 mt-0.5 leading-relaxed">
                  Generate instant, secure checkout links directly within chat conversations.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Webhook Endpoint Card */}
        {webhookUrl && (
          <div className="bg-white rounded-xl border border-zinc-200 p-4 sm:p-6 space-y-3">
            <div className="flex items-center gap-2.5 pb-3 border-b border-zinc-100">
              <Globe className="text-zinc-800 shrink-0" size={18} />
              <div>
                <h3 className="text-xs font-semibold text-zinc-900 uppercase tracking-wider">Shopify Webhook Callback URL</h3>
                <p className="text-[11px] text-zinc-500">Add in your Shopify Custom App to sync real-time store events</p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 p-1.5 sm:p-2 bg-zinc-50 rounded-lg border border-zinc-200">
              <div className="font-mono text-[11px] sm:text-xs text-zinc-700 px-2 py-1 flex-1 break-all select-all min-w-0">
                {displayUrl}
              </div>
              <button
                type="button"
                onClick={copyWebhookUrl}
                className={`inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors flex-shrink-0 ${
                  copied
                    ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                    : "bg-white text-zinc-800 border border-zinc-200 hover:bg-zinc-100"
                }`}
                title={copied ? "Copied!" : "Copy Webhook URL"}
              >
                {copied ? <Check size={13} className="text-emerald-600" /> : <Copy size={13} />}
                <span>{copied ? "Copied" : "Copy URL"}</span>
              </button>
            </div>
          </div>
        )}

        {/* Setup Instructions Box */}
        <div className="p-4 sm:p-5 rounded-xl border border-zinc-200 bg-zinc-50/60 space-y-2.5">
          <div className="flex items-center gap-2 text-xs font-semibold text-zinc-900 uppercase tracking-wider">
            <Info size={14} className="text-zinc-600" />
            <span>Setup Instructions</span>
          </div>
          <ol className="list-decimal pl-4 space-y-1.5 text-xs text-zinc-600 leading-relaxed">
            <li>
              In your Shopify Admin, navigate to <span className="text-zinc-800 font-medium">Settings → Apps and sales channels → Develop apps</span>.
            </li>
            <li>
              Click <span className="text-zinc-800 font-medium">Create an app</span> and configure <span className="text-zinc-800 font-medium">Admin API integration</span>.
            </li>
            <li>
              Grant the scopes: <code className="text-zinc-800 bg-zinc-200/60 px-1 py-0.5 rounded text-[10px] font-mono">read_products</code>, <code className="text-zinc-800 bg-zinc-200/60 px-1 py-0.5 rounded text-[10px] font-mono">read_orders</code>, and <code className="text-zinc-800 bg-zinc-200/60 px-1 py-0.5 rounded text-[10px] font-mono">write_draft_orders</code>.
            </li>
            <li>
              Install the app, copy the <strong className="text-zinc-800">Admin API access token</strong> (starts with <code className="font-mono text-[10px]">shpat_</code>), and save it above.
            </li>
          </ol>
        </div>
      </div>

      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={deleteConfig}
        title="Disconnect Shopify?"
        message="Are you sure you want to disconnect Shopify? The AI agent will no longer be able to track orders or sync product information from this store."
        confirmText="Disconnect"
        isLoading={saving}
      />
    </div>
  );
};

export default ShopifyConfig;
